import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import { IoArrowBack } from "react-icons/io5";
import { Modal } from "@/Components/Common/Tailwind";
import { useAuthContext } from "../../../Components/AuthContext";
import { couponService } from "../../../api/services/coupon.service";
import { voucherService } from "../../../api/services/voucher.service";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import {
  Card,
  CardHeader,
  StatusBadge,
  StatusDot,
  OrderInfo,
  OrderDate,
  ProductName,
  ActionButtons,
  CancelButton,
  DetailButton,
  ContentArea,
} from "../../../Components/Common";
import { LoadingSpinner, EmptyMessage, FilterBadges } from "../../../Components/Common";

const FILTER_OPTIONS = ["전체", "사용가능", "완료"];

type CouponItem = {
  key: string;
  type: "coupon" | "voucher";
  kindLabel: string;
  id: number;
  title: string;
  state: string;
  expireDate: string | null;
  detailText: string;
  uid: string | null;
  image: string | null;
  precautions: string;
  usedTime: string | null;
  gifted: boolean;
  giftable: boolean;
};

async function fetchAllCoupons(accessToken: string | null): Promise<CouponItem[]> {
  const allCoupons: any[] = [];
  let page = 1;
  while (true) {
    const res = await couponService.getCouponsByPage(page);
    const pageData = res.data || {};
    const results = pageData.results || [];
    allCoupons.push(...results);
    if (!pageData.next) break;
    const match = pageData.next.match(/page=(\d+)/);
    if (!match) break;
    page = Number(match[1]);
    if (!page || Number.isNaN(page)) break;
  }

  const voucherRes = await voucherService.getVoucherSendData();
  const vouchers: any[] = voucherRes.data?.vouchers || [];

  return [
    ...allCoupons.map((coupon) => ({
      key: `coupon-${coupon.id}`,
      type: "coupon" as const,
      kindLabel: "할인쿠폰",
      id: coupon.id,
      title: coupon.coupon?.title || coupon.coupon_name || "할인쿠폰",
      state: coupon.state || "사용안함",
      expireDate: coupon.coupon?.expire_date || coupon.expire_date || null,
      detailText:
        coupon.coupon?.discount_type === "price"
          ? `${coupon.coupon?.discount_amount?.toLocaleString?.() || 0}원 할인쿠폰`
          : `${coupon.coupon?.discount_rate || 0}% 할인쿠폰`,
      uid: null,
      image: coupon.coupon?.image || null,
      precautions: coupon.coupon?.descript || "",
      usedTime: coupon.used_time || null,
      gifted: !!coupon.gifted,
      giftable: !!coupon.giftable,
    })),
    ...vouchers.map((voucher) => ({
      key: `voucher-${voucher.id}`,
      type: "voucher" as const,
      kindLabel: "무료쿠폰",
      id: voucher.id,
      title: voucher.name || "무료쿠폰",
      state: voucher.state || "사용안함",
      expireDate: voucher.expire_date || null,
      detailText: "무료쿠폰",
      uid: voucher.uid || null,
      image: voucher.image || null,
      precautions: voucher.precautions || "",
      usedTime: voucher.used_time || null,
      gifted: !!voucher.gifted,
      giftable: !!voucher.giftable,
    })),
  ];
}

export default function CouponBook() {
  const navigate = useNavigate();
  const { accessToken } = useAuthContext();
  const [filter, setFilter] = useState("전체");
  const [selectedVoucher, setSelectedVoucher] = useState<CouponItem | null>(null);
  const [voucherUseModalOpen, setVoucherUseModalOpen] = useState(false);
  const [voucherDetailModalOpen, setVoucherDetailModalOpen] = useState(false);

  const query = useQuery({
    queryKey: ["coupon-book"],
    queryFn: () => fetchAllCoupons(accessToken),
    enabled: !!accessToken,
  });

  const sorted = (query.data ?? [])
    .slice()
    .sort((a, b) => {
      if (a.state === "사용안함" && b.state !== "사용안함") return -1;
      if (a.state !== "사용안함" && b.state === "사용안함") return 1;
      if ((a.expireDate || "") > (b.expireDate || "")) return -1;
      if ((a.expireDate || "") < (b.expireDate || "")) return 1;
      return 0;
    });

  const displayItems = sorted.filter((item) => {
    if (filter === "사용가능") return item.state === "사용안함";
    if (filter === "완료") return item.state !== "사용안함";
    return true;
  });

  const processVoucherUsage = async () => {
    if (!selectedVoucher || selectedVoucher.state !== "사용안함") {
      toast.error("사용 가능한 무료쿠폰이 아닙니다.");
      return;
    }
    try {
      await voucherService.postUserVoucherStateById(selectedVoucher.id);
      toast.success("무료쿠폰이 사용 처리되었습니다.");
      setVoucherUseModalOpen(false);
      setSelectedVoucher(null);
      query.refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "무료쿠폰 사용 처리에 실패했습니다.");
    }
  };

  return (
    <React.Fragment>
      <MobileViewContainer>
        <div className="min-h-screen bg-gray-50 pb-24">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
            >
              <IoArrowBack size={20} />
            </button>
            <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
              쿠폰 내역
            </h1>
          </div>

          <FilterBadges
            options={FILTER_OPTIONS}
            activeFilter={filter}
            onFilterChange={setFilter}
          />

          <ContentArea>
            {/* Coupon book download shortcut */}
            <div className="mb-4" style={{ padding: "0 2px" }}>
              <button
                type="button"
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-left shadow-sm"
                onClick={() => navigate("/coupon-book")}
              >
                <div className="text-xs text-gray-500">다운로드</div>
                <div className="text-sm font-semibold text-gray-900">쿠폰북 다운로드</div>
              </button>
            </div>

            {query.isLoading ? (
              <LoadingSpinner height="200px" />
            ) : displayItems.length === 0 ? (
              <EmptyMessage message="할인쿠폰/무료쿠폰 내역이 없습니다." />
            ) : (
              displayItems.map((coupon) => (
                <Card key={coupon.key} style={{ opacity: coupon.state === "사용안함" ? 1 : 0.65 }}>
                  <CardHeader>
                    <StatusBadge status={coupon.state}>
                      <StatusDot status={coupon.state} />
                      {coupon.state}
                    </StatusBadge>
                    <OrderDate>
                      {coupon.expireDate ? `~${coupon.expireDate}` : "만료기한 없음"}
                    </OrderDate>
                  </CardHeader>

                  <ProductName>
                    [{coupon.kindLabel}] {coupon.title}
                  </ProductName>

                  <div style={{ color: "#f6475f", fontWeight: 600, marginBottom: 8 }}>
                    {coupon.detailText}
                  </div>

                  <ActionButtons>
                    {coupon.type === "coupon" ? (
                      <DetailButton onClick={() => navigate("/coupon-book?tab=coupon")}>
                        북 보기
                      </DetailButton>
                    ) : (
                      <>
                        <DetailButton
                          onClick={() => {
                            setSelectedVoucher(coupon);
                            setVoucherUseModalOpen(true);
                          }}
                          disabled={coupon.state !== "사용안함"}
                        >
                          {coupon.state === "사용안함" ? "사용하기" : "사용불가"}
                        </DetailButton>
                        <CancelButton
                          onClick={() => {
                            setSelectedVoucher(coupon);
                            setVoucherDetailModalOpen(true);
                          }}
                        >
                          상세보기
                        </CancelButton>
                      </>
                    )}
                  </ActionButtons>
                </Card>
              ))
            )}
          </ContentArea>
        </div>
      </MobileViewContainer>

      {/* Voucher use modal */}
      <Modal
        isOpen={voucherUseModalOpen}
        toggle={() => {
          setVoucherUseModalOpen(false);
          setSelectedVoucher(null);
        }}
        centered
      >
        <div className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <h5 className="text-base font-semibold text-gray-900">무료쿠폰 사용하기</h5>
            <button
              type="button"
              className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-500"
              onClick={() => {
                setVoucherUseModalOpen(false);
                setSelectedVoucher(null);
              }}
            >
              닫기
            </button>
          </div>

          {selectedVoucher && (
            <>
              <div className="mb-4 flex justify-center">
                {selectedVoucher.uid ? <QRCodeSVG value={selectedVoucher.uid} /> : null}
              </div>
              <div className="space-y-2 rounded border border-gray-200 p-3 text-sm text-gray-700">
                <div>
                  <span className="mr-2 font-semibold">상품명</span>
                  <span>{selectedVoucher.title}</span>
                </div>
                <div>
                  <span className="mr-2 font-semibold">상태</span>
                  <span>{selectedVoucher.state}</span>
                </div>
                <div>
                  <span className="mr-2 font-semibold">만료일</span>
                  <span>{selectedVoucher.expireDate || "없음"}</span>
                </div>
              </div>
              {selectedVoucher.image && (
                <div className="mt-3">
                  <img
                    src={selectedVoucher.image}
                    alt={selectedVoucher.title}
                    className="h-28 w-full rounded border border-gray-200 object-cover"
                  />
                </div>
              )}
              <div className="mt-3 rounded border border-gray-200 p-3">
                <div className="mb-1 text-sm font-semibold text-gray-700">주의사항</div>
                <div
                  className="text-xs text-gray-600"
                  dangerouslySetInnerHTML={{
                    __html: selectedVoucher.precautions || "주의사항이 없습니다.",
                  }}
                />
              </div>
              <div className="mt-4 flex justify-center gap-2">
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700"
                  onClick={() => {
                    setVoucherUseModalOpen(false);
                    setSelectedVoucher(null);
                  }}
                >
                  닫기
                </button>
                <button
                  type="button"
                  className="rounded bg-rose-500 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={selectedVoucher.state !== "사용안함"}
                  onClick={processVoucherUsage}
                >
                  직원확인
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Voucher detail modal */}
      <Modal
        isOpen={voucherDetailModalOpen}
        toggle={() => {
          setVoucherDetailModalOpen(false);
          setSelectedVoucher(null);
        }}
        centered
      >
        <div className="p-4">
          <div className="mb-4 flex items-center justify-between">
            <h5 className="text-base font-semibold text-gray-900">무료쿠폰 상세정보</h5>
            <button
              type="button"
              className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-500"
              onClick={() => {
                setVoucherDetailModalOpen(false);
                setSelectedVoucher(null);
              }}
            >
              닫기
            </button>
          </div>

          {selectedVoucher && (
            <>
              {selectedVoucher.image && (
                <div className="mb-3">
                  <img
                    src={selectedVoucher.image}
                    alt={selectedVoucher.title}
                    className="h-36 w-full rounded border border-gray-200 object-cover"
                  />
                </div>
              )}
              <div className="space-y-2 rounded border border-gray-200 p-3 text-sm text-gray-700">
                <div>
                  <span className="mr-2 font-semibold">상품명</span>
                  <span>{selectedVoucher.title}</span>
                </div>
                <div>
                  <span className="mr-2 font-semibold">상태</span>
                  <span>{selectedVoucher.state}</span>
                </div>
                <div>
                  <span className="mr-2 font-semibold">만료일</span>
                  <span>{selectedVoucher.expireDate || "없음"}</span>
                </div>
                {selectedVoucher.usedTime && (
                  <div>
                    <span className="mr-2 font-semibold">사용시간</span>
                    <span>{selectedVoucher.usedTime}</span>
                  </div>
                )}
              </div>
              <div className="mt-3 rounded border border-gray-200 p-3">
                <div className="mb-1 text-sm font-semibold text-gray-700">주의사항</div>
                <div
                  className="text-xs text-gray-600"
                  dangerouslySetInnerHTML={{
                    __html: selectedVoucher.precautions || "주의사항이 없습니다.",
                  }}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
}
